/**
 * THE REFUSALS OF A ROLE ASSIGNMENT → HEBREW. OA-0001 §3.3 / §3.11, 08 §7.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * "YOU MAY NOT GRANT THESE" IS ACTIONABLE. "FAILED" IS THE DEFECT.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `apps/api/src/domain/capability-ceiling.ts` says why it returns a list and not a boolean:
 *
 *   > *Returns the EXCESS, not just a boolean: OA-0001 §3.11 and 08 §7 both require an actionable
 *   > refusal, and "you may not grant these three capabilities" is actionable in a way that
 *   > `403 forbidden` is not.*
 *
 * A list of capability CODES is not actionable to a bookkeeper, so this module is the other half of
 * that sentence: it turns the excess into the catalogue's own Hebrew, and it says WHY the granter
 * could not confer them — which is a different sentence for each of the two ceiling refusals and is
 * the part a user actually needs:
 *
 *   `exceeds_granter_capabilities`          you cannot give away what you do not have
 *   `delegation_capability_not_delegatable` you have it, but it decides who gets what, and passing
 *                                           that on would lift your own ceiling
 *
 * ── WHAT THIS MODULE MAY AND MAY NOT DO ─────────────────────────────────────────────────────────
 *
 * It maps a decision that has ALREADY BEEN MADE into words. It does not compare the granter's
 * capabilities to anything, it never sees `ctx.capabilities`, and there is no function here that
 * could answer "may I grant this?" — that question has exactly one implementation, in
 * `checkDelegationCeiling` on the server, and a second one here would be family F9 with a Hebrew
 * accent: two answers to one question, drifting apart at their own pace.
 *
 * ── AND NO CODE EVER REACHES THE SCREEN ─────────────────────────────────────────────────────────
 *
 * 09 §9.1 V3 allows exactly one technical string on a customer's screen, and it is a support
 * reference. `capabilityDescriptionHe` is `null` for a capability this build has no Hebrew for — the
 * tenant capability set is open by design, a module contributes its own when it migrates — so an
 * un-named capability is COUNTED and written out as a gap, exactly as the roles catalogue below it
 * does. It is never printed as its code, and it is never silently dropped either: a refusal that
 * listed two of three capabilities would be a refusal the user cannot act on.
 */

import type { RoleAssignmentRefusal, RoleAssignmentRefusalReason } from './contract';
import { capabilityDescriptionHe } from './adminVocabulary';

export interface RefusedCapabilities {
  /** The catalogue's own Hebrew, in the order the server sorted the codes. Deduplicated. */
  readonly namedHe: readonly string[];
  /** How many refused capabilities this build has no Hebrew for. Counted, never printed as a code. */
  readonly unnamedCount: number;
}

/**
 * The excess, as names. Deduplicated because the same capability can arrive from two roles, and a
 * refusal that named it twice reads as two separate problems.
 */
export function refusedCapabilities(excess: readonly string[]): RefusedCapabilities {
  const namedHe: string[] = [];
  const seen = new Set<string>();
  let unnamedCount = 0;

  for (const code of excess) {
    if (seen.has(code)) continue;
    seen.add(code);
    const he = capabilityDescriptionHe(code);
    if (he === null) {
      unnamedCount += 1;
      continue;
    }
    if (!namedHe.includes(he)) namedHe.push(he);
  }

  return { namedHe, unnamedCount };
}

export interface RoleAssignmentRefusalText {
  readonly titleHe: string;
  /** WHY the granter could not confer them. One sentence, in office Hebrew, never a code. */
  readonly explanationHe: string;
  /**
   * The heading above the list of refused capabilities. `null` when the refusal is not about
   * capabilities at all — and also when it is, but no capability could be named, because a heading
   * over an empty list is a promise the screen does not keep.
   */
  readonly refusedHeadingHe: string | null;
  readonly namedHe: readonly string[];
  /** `null` unless this build lacks Hebrew for some of them; then it says how many, in words. */
  readonly unnamedHe: string | null;
  /**
   * OA-0001 §3.11's other half, applied to permissions rather than to companies: an administrator
   * told only "refused" reasonably fears that something was half-done. Nothing was.
   */
  readonly unchangedHe: string;
  /** The written next step. 09 §5.8: every state has one, and it is never "try again". */
  readonly nextStepHe: string;
}

/**
 * The two ceiling refusals share a next step, and it is deliberately two options rather than one:
 * an office administrator who has hit their ceiling can either pick a narrower role or ask the
 * person whose ceiling is higher. Telling them only the first would be telling them to give up.
 */
const CEILING_NEXT_STEP_HE =
  'אפשר לבחור תפקידים שאינם כוללים את ההרשאות האלה, או לבקש מבעלים של המשרד לבצע את ההענקה.';

/** Nothing was written. Stated for every refusal, because a partial grant is what people fear. */
const UNCHANGED_HE = 'לא נשמר שינוי. ההרשאות של המשתמש נשארו כפי שהיו לפני הבקשה.';

interface RefusalCopy {
  readonly titleHe: string;
  readonly explanationHe: string;
  readonly refusedHeadingHe: string | null;
  readonly nextStepHe: string;
}

const REFUSAL_HE: Readonly<Record<RoleAssignmentRefusalReason, RefusalCopy>> = {
  /**
   * The subset rule. A principal may hand on what they hold, and nothing more — otherwise one route
   * and one request turn `tenant.role.manage` into the entire tenant-side catalogue.
   */
  exceeds_granter_capabilities: {
    titleHe: 'לא ניתן להעניק הרשאות שאינן קיימות בתפקידים שלך',
    explanationHe:
      'אפשר להעניק למשתמש אחר רק הרשאות שקיימות בתפקידים שלך. התפקידים שנבחרו כוללים הרשאות ' +
      'שאינן כלולות בתפקידים שלך, ולכן לא ניתן להעניק אותן הלאה.',
    refusedHeadingHe: 'ההרשאות שלא ניתן להעניק',
    nextStepHe: CEILING_NEXT_STEP_HE,
  },
  /**
   * The shorter path the subset rule alone does not close: a capability that decides WHO HOLDS WHAT
   * is one assignment away from lifting the ceiling itself, so holding it is not enough to pass it on.
   */
  delegation_capability_not_delegatable: {
    titleHe: 'לא ניתן להעביר הלאה הרשאות שקובעות מי מקבל הרשאות',
    explanationHe:
      'ההרשאות האלה קיימות בתפקידים שלך, אך הן קובעות מי מקבל הרשאות במשרד. העברה שלהן למשתמש ' +
      'אחר הייתה מבטלת את הגבול שנקבע לתפקיד שלך, ולכן היא שמורה לבעלים של המשרד בלבד.',
    refusedHeadingHe: 'ההרשאות שלא ניתן להעביר הלאה',
    nextStepHe: CEILING_NEXT_STEP_HE,
  },
  /**
   * Not about capabilities, so there is no list. The sentence says the thing that matters instead:
   * a request for three roles that could only deliver two delivered none, on purpose.
   */
  unknown_role: {
    titleHe: 'אחד התפקידים שנבחרו אינו קיים במשרד',
    explanationHe:
      'רשימת התפקידים נקראת מחדש בעת ההענקה. תפקיד שנמחק או ששמו שונה מאז שהמסך נטען אינו מוענק, ' +
      'ובמקרה כזה לא מוענק אף אחד מהתפקידים שנבחרו — הענקה חלקית היא תוצאה שאיש לא ביקש.',
    refusedHeadingHe: null,
    nextStepHe: 'רענון המסך ובחירת התפקידים מחדש מתוך הרשימה המעודכנת.',
  },
  membership_not_found: {
    titleHe: 'המשתמש אינו נמצא ברשימת המשרד',
    explanationHe:
      'ייתכן שהמשתמש הוסר מהמשרד או שהרשומה שלו השתנתה מאז שהמסך נטען. הענקת תפקיד נעשית תמיד ' +
      'למשתמש שנמצא ברשימת המשרד, ולכן הבקשה לא בוצעה.',
    refusedHeadingHe: null,
    nextStepHe: 'רענון רשימת המשתמשים ובחירת המשתמש מחדש.',
  },
};

/** Exposed for the mirror test only — an unmapped refusal is an English code on a Hebrew screen. */
export function mappedRefusalReasons(): readonly RoleAssignmentRefusalReason[] {
  return Object.keys(REFUSAL_HE) as RoleAssignmentRefusalReason[];
}

/**
 * The refusal, rendered. Four things every time — what was refused, why, that nothing changed, and
 * what to do — plus the capability names when the refusal is about capabilities.
 */
export function roleAssignmentRefusalText(
  refusal: RoleAssignmentRefusal,
): RoleAssignmentRefusalText {
  const copy = REFUSAL_HE[refusal.reason];
  const { namedHe, unnamedCount } = refusedCapabilities(refusal.excess);
  const hasList = namedHe.length > 0 || unnamedCount > 0;

  return {
    titleHe: copy.titleHe,
    explanationHe: copy.explanationHe,
    refusedHeadingHe: hasList ? copy.refusedHeadingHe : null,
    namedHe,
    // The same words the roles catalogue uses for the same gap, so a reader meets one phrasing.
    unnamedHe:
      unnamedCount === 0
        ? null
        : `ועוד ${unnamedCount} הרשאות שאין להן עדיין שם בעברית במסך הזה.`,
    unchangedHe: UNCHANGED_HE,
    nextStepHe: copy.nextStepHe,
  };
}

/**
 * WHAT THE SERVER SAYS WHEN THE REFUSAL ITSELF COULD NOT TRAVEL.
 *
 * The assignment route answers a refused assignment with a conflict and a body naming the reason.
 * `api/client.ts`'s taxonomy is binding and it keeps only a support reference from that body, so on
 * this path the screen knows two true things and no more: the server REFUSED, and because it refused
 * before writing, NOTHING CHANGED. It says exactly those two and does not guess which of the four
 * refusals it was — a screen that guessed would be telling an office it hit a ceiling when its
 * colleague had merely been removed from the list.
 *
 * This is not a smaller version of the text above; it is a different, honest claim, and the gap
 * between them is measured by `TenantRolesScreen`'s header.
 */
export const REFUSED_WITHOUT_DETAIL_HE = {
  titleHe: 'ההענקה לא בוצעה',
  bodyHe:
    'השרת סירב לבקשה ולא נשמר שינוי — ההרשאות של המשתמש נשארו כפי שהיו. הסיבה המדויקת אינה מגיעה ' +
    'למסך הזה בבנייה הנוכחית.',
  nextStepHe:
    'הענקה מוגבלת להרשאות שקיימות בתפקידים שלך, והרשאות שקובעות מי מקבל הרשאות שמורות לבעלים של ' +
    'המשרד. אפשר לבדוק אילו הרשאות כלולות בתפקידים שנבחרו ולנסות שוב עם תפקיד מצומצם יותר.',
} as const;
